/* eslint-disable complexity */
import classNames from "classnames";
import * as React from "react";
import { CSSProperties } from "react";

import { isResponsiveProp, ResponsiveProp } from "../Theme/ResponsiveProp";

// eslint-disable-next-line import/no-unassigned-import
import "./native-styles.css";

export interface IProps {
  /** Class name applied to the svg tag */
  className?: string;
  /** data-test-id applied to the svg tag */
  "data-test-id"?: string;
  /**
   * Aria label for the skeleton loader
   */
  ariaLabel?: string;
  animated?: boolean;
  width?: ResponsiveProp<CSSProperties["width"]>;
  height?: ResponsiveProp<CSSProperties["height"]>;
  aspectRatio?: ResponsiveProp<CSSProperties["aspectRatio"]>;
  borderRadius?: ResponsiveProp<CSSProperties["borderRadius"]>;
}

/**
 * @trackcomponent
 */
export const LoadingSkeletonBase: React.FC<IProps> = ({
  className,
  ["data-test-id"]: dataTestId,
  ariaLabel,
  animated = true,
  width,
  height,
  aspectRatio,
  borderRadius,
}) => {
  const vars: Record<string, string | undefined> = {};
  const classes = [];
  if (isResponsiveProp(width)) {
    vars["--f_ls_width_default"] = width.mobileAndAbove?.toString();
    classes.push("f_ls_width_default");
    if (width.tabletAndAbove) {
      vars["--f_ls_width_tablet"] = width.tabletAndAbove?.toString();
      classes.push("f_ls_width_tablet");
    }
    if (width.desktopAndAbove) {
      vars["--f_ls_width_desktop"] = width.desktopAndAbove?.toString();
      classes.push("f_ls_width_desktop");
    }
    if (width.xLargeAndAbove) {
      vars["--f_ls_width_xlarge"] = width.xLargeAndAbove?.toString();
      classes.push("f_ls_width_xlarge");
    }
    if (width.xxLargeDesktop) {
      vars["--f_ls_width_xxlarge"] = width.xxLargeDesktop?.toString();
      classes.push("f_ls_width_xxlarge");
    }
  } else {
    vars["--f_ls_width_default"] = width?.toString();
    classes.push("f_ls_width_default");
  }
  if (isResponsiveProp(height)) {
    vars["--f_ls_height_default"] = height.mobileAndAbove?.toString();
    classes.push("f_ls_height_default");
    if (height.tabletAndAbove) {
      vars["--f_ls_height_tablet"] = height.tabletAndAbove?.toString();
      classes.push("f_ls_height_tablet");
    }
    if (height.desktopAndAbove) {
      vars["--f_ls_height_desktop"] = height.desktopAndAbove?.toString();
      classes.push("f_ls_height_desktop");
    }
    if (height.xLargeAndAbove) {
      vars["--f_ls_height_xlarge"] = height.xLargeAndAbove?.toString();
      classes.push("f_ls_height_xlarge");
    }
    if (height.xxLargeDesktop) {
      vars["--f_ls_height_xxlarge"] = height.xxLargeDesktop?.toString();
      classes.push("f_ls_height_xxlarge");
    }
  } else {
    vars["--f_ls_height_default"] = height?.toString();
    classes.push("f_ls_height_default");
  }
  if (isResponsiveProp(aspectRatio)) {
    vars["--f_ls_aspect_ratio_default"] =
      aspectRatio.mobileAndAbove?.toString();
    classes.push("f_ls_aspect_ratio_default");
    if (aspectRatio.tabletAndAbove) {
      vars["--f_ls_aspect_ratio_tablet"] =
        aspectRatio.tabletAndAbove?.toString();
      classes.push("f_ls_aspect_ratio_tablet");
    }
    if (aspectRatio.desktopAndAbove) {
      vars["--f_ls_aspect_ratio_desktop"] =
        aspectRatio.desktopAndAbove?.toString();
      classes.push("f_ls_aspect_ratio_desktop");
    }
    if (aspectRatio.xLargeAndAbove) {
      vars["--f_ls_aspect_ratio_xlarge"] =
        aspectRatio.xLargeAndAbove?.toString();
      classes.push("f_ls_aspect_ratio_xlarge");
    }
    if (aspectRatio.xxLargeDesktop) {
      vars["--f_ls_aspect_ratio_xxlarge"] =
        aspectRatio.xxLargeDesktop?.toString();
      classes.push("f_ls_aspect_ratio_xxlarge");
    }
  } else {
    vars["--f_ls_aspect_ratio_default"] = aspectRatio?.toString();
    classes.push("f_ls_aspect_ratio_default");
  }
  if (isResponsiveProp(borderRadius)) {
    vars["--f_ls_border_radius_default"] =
      borderRadius.mobileAndAbove?.toString();
    classes.push("f_ls_border_radius_default");
    if (borderRadius.tabletAndAbove) {
      vars["--f_ls_border_radius_tablet"] =
        borderRadius.tabletAndAbove?.toString();
      classes.push("f_ls_border_radius_tablet");
    }
    if (borderRadius.desktopAndAbove) {
      vars["--f_ls_border_radius_desktop"] =
        borderRadius.desktopAndAbove?.toString();
      classes.push("f_ls_border_radius_desktop");
    }
    if (borderRadius.xLargeAndAbove) {
      vars["--f_ls_border_radius_xlarge"] =
        borderRadius.xLargeAndAbove?.toString();
      classes.push("f_ls_border_radius_xlarge");
    }
    if (borderRadius.xxLargeDesktop) {
      vars["--f_ls_border_radius_xxlarge"] =
        borderRadius.xxLargeDesktop?.toString();
      classes.push("f_ls_border_radius_xxlarge");
    }
  } else {
    vars["--f_ls_border_radius_default"] = borderRadius?.toString();
    classes.push("f_ls_border_radius_default");
  }
  return (
    <div
      role="status"
      aria-label={ariaLabel}
      data-test-id={dataTestId}
      style={vars}
      className={classNames(className, "f_ls_container", classes)}
    >
      <div className={classNames(className, "f_ls_static", classes)}>
        {animated ? (
          <div className={classNames(className, "f_ls_animated", classes)} />
        ) : null}
      </div>
    </div>
  );
};
